import { useState } from "react";
import { Button } from "../ui/button";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, Image as ImageIcon } from "@phosphor-icons/react";

/**
 * BackfillLogoPinCard — one-shot data migration trigger.
 *
 * Enables the "use logo on map pin" flag for every Pro owner who has
 * uploaded a logo but never flipped the secondary toggle. Idempotent —
 * safe to re-run; the backend filter excludes already-on rows and
 * admin-disabled rows.
 *
 * Lives in the Truck-help-box accordion so it doesn't clutter the
 * main admin dashboard.
 */
export default function BackfillLogoPinCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!window.confirm(
      "Enable the logo map pin for every Pro owner who has uploaded a logo? "
      + "Admin-disabled trucks are skipped. Safe to re-run."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/trucks/backfill-logo-pin");
      setResult(data);
      const verb = data.modified === 1 ? "truck" : "trucks";
      toast.success(`Logo pin enabled on ${data.modified} ${verb} (${data.matched} matched).`);
    } catch (e) {
      toast.error(formatApiError(e, "Backfill failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-background/50 p-4 space-y-3" data-testid="backfill-logo-pin-card">
      <div className="flex items-start gap-2">
        <ImageIcon size={18} weight="duotone" className="text-primary mt-0.5 flex-none" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">Backfill logo map pins</div>
          <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">
            For every Pro owner who has uploaded a logo but never flipped the
            secondary &ldquo;Use my logo on the map pin&rdquo; toggle on, this
            one-shot migration turns it on. Trucks where admin previously
            disabled the logo pin are skipped. Re-running is safe — rows that
            are already on are no-ops.
          </p>
        </div>
      </div>
      <Button
        size="sm"
        onClick={run}
        disabled={busy}
        data-testid="backfill-logo-pin-run"
      >
        <ArrowsClockwise size={14} weight="bold" className="mr-1" />
        {busy ? "Running…" : "Run backfill"}
      </Button>
      {result && (
        <div className="text-[11px] text-muted-foreground" data-testid="backfill-logo-pin-result">
          Last run: matched {result.matched} draft{result.matched === 1 ? "" : "s"} ·
          flipped on {result.modified} truck{result.modified === 1 ? "" : "s"}.
        </div>
      )}
    </div>
  );
}
